import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import {
  getLicenses, getLicenseAddons, getAddonCatalog, assignAddon, cancelAddon,
  rechargeStamps, getStampTransactions, getStampBalance, getAdminInvoicesBySerial, getBusiness
} from '../../services/api';
import {
  ArrowLeft, Package, Plus, X, Trash2, Receipt, RefreshCw, ChevronLeft, ChevronRight, FileText, Sliders
} from 'lucide-react';
import { toast } from 'sonner';
import StampAdjustModal from './licencias/StampAdjustModal';

const STATUS_LABELS = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700' },
  activated: { label: 'Activada', color: 'bg-blue-100 text-blue-700' },
  linked: { label: 'Ligada', color: 'bg-green-100 text-green-700' },
  expired: { label: 'Expirada', color: 'bg-slate-100 text-slate-600' },
  revoked: { label: 'Revocada', color: 'bg-red-100 text-red-700' },
};

const TX_TYPE_LABELS = {
  recharge: { label: 'Recarga', color: 'bg-green-100 text-green-700' },
  consumption: { label: 'Consumo', color: 'bg-orange-100 text-orange-700' },
  adjustment: { label: 'Ajuste', color: 'bg-blue-100 text-blue-700' },
  refund: { label: 'Reembolso', color: 'bg-purple-100 text-purple-700' },
};

const ADDON_STATUS = {
  active: { label: 'Activo', color: 'bg-green-100 text-green-700' },
  suspended: { label: 'Suspendido', color: 'bg-yellow-100 text-yellow-700' },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
};

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return d; }
};

const LicenciaGestion = () => {
  const { serial } = useParams();
  const navigate = useNavigate();

  const [license, setLicense] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [loading, setLoading] = useState(true);

  // Addons
  const [addons, setAddons] = useState([]);
  const [addonCatalog, setAddonCatalog] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState({ addon_code: '', notes: '' });

  // Stamps
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [txPage, setTxPage] = useState(0);
  const [balance, setBalance] = useState({ available: 0, total_purchased: 0, total_used: 0 });
  const [rechargeForm, setRechargeForm] = useState({ quantity: '', description: '' });
  const [recharging, setRecharging] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);

  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [invoiceTotal, setInvoiceTotal] = useState(0);
  const [invoicePage, setInvoicePage] = useState(0);

  useEffect(() => {
    loadAll();
  }, [serial]);

  useEffect(() => {
    loadTransactions();
  }, [txPage]);

  useEffect(() => {
    if (serial) loadInvoices();
  }, [invoicePage]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const licRes = await getLicenses({ serial_number: serial });
      const lics = licRes.data?.licenses || [];
      const lic = lics.find(l => l.serial_number === serial) || null;
      setLicense(lic);

      if (lic?.business_id) {
        getBusiness(lic.business_id).then(r => setBusinessName(r.data?.name || '')).catch(() => {});
      }

      await Promise.all([loadAddons(), loadBalance(), loadTransactions(), loadInvoices()]);

      try {
        const catRes = await getAddonCatalog();
        setAddonCatalog(catRes.data?.data || []);
      } catch { setAddonCatalog([]); }

    } catch (error) {
      console.error('Error loading license:', error);
      toast.error('Error al cargar datos de la licencia');
    } finally {
      setLoading(false);
    }
  };

  const loadAddons = async () => {
    try {
      const res = await getLicenseAddons(serial);
      setAddons(res.data?.data || []);
    } catch { setAddons([]); }
  };

  const loadBalance = async () => {
    try {
      const res = await getStampBalance(serial);
      setBalance({
        available: res.data?.available || 0,
        total_purchased: res.data?.total_purchased || 0,
        total_used: res.data?.total_used || 0,
      });
    } catch { setBalance({ available: 0, total_purchased: 0, total_used: 0 }); }
  };

  const loadTransactions = async () => {
    try {
      const res = await getStampTransactions(serial, { skip: txPage * 20, limit: 20 });
      setTransactions(res.data?.data || []);
      setTxTotal(res.data?.total || 0);
    } catch {
      setTransactions([]);
    }
  };

  const loadInvoices = async () => {
    try {
      const res = await getAdminInvoicesBySerial(serial, { skip: invoicePage * 20, limit: 20 });
      setInvoices(res.data?.items || []);
      setInvoiceTotal(res.data?.total || 0);
    } catch { setInvoices([]); }
  };

  const handleAssignAddon = async (e) => {
    e.preventDefault();
    if (!assignForm.addon_code) {
      toast.error('Selecciona un addon');
      return;
    }
    try {
      await assignAddon(serial, assignForm);
      toast.success('Addon asignado exitosamente');
      setShowAssignModal(false);
      setAssignForm({ addon_code: '', notes: '' });
      await loadAddons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar addon');
    }
  };

  const handleCancelAddon = async (code) => {
    if (!window.confirm(`¿Desactivar addon "${code}" de esta licencia?`)) return;
    try {
      await cancelAddon(serial, code);
      toast.success('Addon desactivado');
      await loadAddons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al desactivar addon');
    }
  };

  const handleRecharge = async (e) => {
    e.preventDefault();
    const qty = parseInt(rechargeForm.quantity);
    if (!qty || qty <= 0) {
      toast.error('Ingresa una cantidad válida');
      return;
    }
    setRecharging(true);
    try {
      const res = await rechargeStamps({
        serial_number: serial,
        quantity: qty,
        description: rechargeForm.description || `Recarga manual de ${qty} timbres`,
        reference_type: 'manual',
      });
      toast.success(`${qty} timbres recargados. Nuevo saldo: ${res.data.new_balance}`);
      setRechargeForm({ quantity: '', description: '' });
      setTxPage(0);
      await Promise.all([loadBalance(), loadTransactions()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al recargar timbres');
    } finally {
      setRecharging(false);
    }
  };

  // Available addons to assign (not already active)
  const activeAddonCodes = addons.filter(a => a.status === 'active').map(a => a.addon_code);
  const availableAddons = addonCatalog.filter(a => a.is_active && !activeAddonCodes.includes(a.code));

  const st = license ? (STATUS_LABELS[license.status] || { label: license.status, color: 'bg-slate-100 text-slate-600' }) : {};

  const pctAvail = balance.total_purchased > 0
    ? Math.round((balance.available / balance.total_purchased) * 100)
    : 0;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700" />
        </div>
      </DashboardLayout>
    );
  }

  if (!license) {
    return (
      <DashboardLayout>
        <div className="text-center py-20">
          <p className="text-slate-500 mb-4">Licencia no encontrada</p>
          <button onClick={() => navigate('/superadmin/licencias-pos')} className="text-teal-700 hover:underline">
            Volver a licencias
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/superadmin/licencias-pos')} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <code className="text-lg font-mono font-bold text-slate-900">{serial}</code>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">{businessName || 'Sin negocio asignado'}</p>
          </div>
        </div>

        {/* ==================== ADDONS SECTION ==================== */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 text-teal-700" />
              <h2 className="text-lg font-bold text-slate-900">Addons</h2>
            </div>
            {availableAddons.length > 0 && (
              <button
                onClick={() => setShowAssignModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-700 text-white text-sm rounded-lg hover:bg-teal-800"
              >
                <Plus className="w-4 h-4" /> Asignar addon
              </button>
            )}
          </div>

          {addons.length === 0 ? (
            <div className="px-6 py-8 text-center text-slate-500">
              No hay addons asignados a esta licencia.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Código</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Nombre</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Activado</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {addons.map((addon) => {
                    const as2 = ADDON_STATUS[addon.status] || { label: addon.status, color: 'bg-slate-100 text-slate-600' };
                    return (
                      <tr key={addon.id || addon.addon_code} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-mono text-slate-700">{addon.addon_code}</td>
                        <td className="px-4 py-3 text-sm text-slate-900">{addon.addon_name}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${as2.color}`}>{as2.label}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">{formatDate(addon.activated_at)}</td>
                        <td className="px-4 py-3 text-right">
                          {addon.status === 'active' && (
                            <button
                              onClick={() => handleCancelAddon(addon.addon_code)}
                              className="p-1.5 hover:bg-red-50 rounded text-red-500"
                              title="Desactivar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* ==================== STAMPS SECTION ==================== */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-teal-700" />
              <h2 className="text-lg font-bold text-slate-900">Timbres de Facturación</h2>
            </div>
            <button onClick={() => setShowAdjustModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-700 text-sm rounded-lg hover:bg-slate-200 font-medium transition-colors">
              <Sliders className="w-3.5 h-3.5" /> Ajustar
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Balance Card + Recharge */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Balance */}
              <div className="bg-slate-50 rounded-lg p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600">Saldo de timbres</span>
                  <span className="text-2xl font-bold text-slate-900">{balance.available.toLocaleString('es-MX')}</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2.5">
                  <div
                    className="bg-teal-600 h-2.5 rounded-full transition-all"
                    style={{ width: `${Math.min(pctAvail, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>Usados: {balance.total_used.toLocaleString('es-MX')}</span>
                  <span>Comprados: {balance.total_purchased.toLocaleString('es-MX')}</span>
                </div>
              </div>

              {/* Recharge Form */}
              <div className="bg-slate-50 rounded-lg p-5">
                <form onSubmit={handleRecharge} className="space-y-3">
                  <label className="block text-sm font-medium text-slate-700">Recargar timbres</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="1"
                      placeholder="Cantidad"
                      value={rechargeForm.quantity}
                      onChange={(e) => setRechargeForm({ ...rechargeForm, quantity: e.target.value })}
                      className="w-28 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      required
                    />
                    <input
                      type="text"
                      placeholder="Descripción (opcional)"
                      value={rechargeForm.description}
                      onChange={(e) => setRechargeForm({ ...rechargeForm, description: e.target.value })}
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={recharging}
                    className="flex items-center gap-1.5 px-4 py-2 bg-teal-700 text-white text-sm rounded-lg hover:bg-teal-800 disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${recharging ? 'animate-spin' : ''}`} />
                    {recharging ? 'Recargando...' : 'Recargar'}
                  </button>
                </form>
              </div>
            </div>

            {/* Transactions Table */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Historial de transacciones</h3>
              {transactions.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">Sin transacciones registradas.</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Tipo</th>
                          <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Cantidad</th>
                          <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Saldo</th>
                          <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Descripción</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {transactions.map((tx) => {
                          const tt = TX_TYPE_LABELS[tx.type] || { label: tx.type, color: 'bg-slate-100 text-slate-600' };
                          return (
                            <tr key={tx.id} className="hover:bg-slate-50">
                              <td className="px-4 py-2 text-sm text-slate-500">{formatDate(tx.created_at)}</td>
                              <td className="px-4 py-2">
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${tt.color}`}>{tt.label}</span>
                              </td>
                              <td className={`px-4 py-2 text-sm text-right font-medium ${tx.quantity > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {tx.quantity > 0 ? '+' : ''}{tx.quantity}
                              </td>
                              <td className="px-4 py-2 text-sm text-right text-slate-700">{tx.balance_after}</td>
                              <td className="px-4 py-2 text-sm text-slate-600 max-w-[250px] truncate">{tx.description || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {txTotal > 20 && (
                    <div className="flex items-center justify-between mt-3 px-2">
                      <span className="text-xs text-slate-500">
                        {txPage * 20 + 1}-{Math.min((txPage + 1) * 20, txTotal)} de {txTotal}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setTxPage(Math.max(0, txPage - 1))}
                          disabled={txPage === 0}
                          className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setTxPage(txPage + 1)}
                          disabled={(txPage + 1) * 20 >= txTotal}
                          className="p-1 hover:bg-slate-100 rounded disabled:opacity-30"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* ==================== INVOICES SECTION ==================== */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-700" />
              <h2 className="text-lg font-bold text-slate-900">Historial de Facturas</h2>
            </div>
            <span className="text-sm text-slate-500">{invoiceTotal} facturas</span>
          </div>

          {invoices.length === 0 ? (
            <div className="px-6 py-8 text-center text-slate-500">
              No hay facturas registradas para esta licencia.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">UUID</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Tipo</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Serie-Folio</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Receptor</th>
                    <th className="text-right px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Total</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                    <th className="text-left px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoices.map((inv) => {
                    const typeLabels = { I: { l: 'Ingreso', c: 'bg-blue-100 text-blue-700' }, E: { l: 'Egreso', c: 'bg-orange-100 text-orange-700' }, P: { l: 'Pago', c: 'bg-purple-100 text-purple-700' }, T: { l: 'Traslado', c: 'bg-slate-100 text-slate-600' } };
                    const statusLabels = { active: { l: 'Activa', c: 'bg-green-100 text-green-700' }, cancelled: { l: 'Cancelada', c: 'bg-red-100 text-red-700' }, cancel_pending: { l: 'Pend. Cancelar', c: 'bg-yellow-100 text-yellow-700' } };
                    const t = typeLabels[inv.type] || { l: inv.type, c: 'bg-slate-100 text-slate-600' };
                    const s = statusLabels[inv.status] || { l: inv.status, c: 'bg-slate-100 text-slate-600' };
                    return (
                      <tr key={inv.id || inv.uuid_fiscal} className="hover:bg-slate-50">
                        <td className="px-4 py-2 text-xs font-mono text-slate-600" title={inv.uuid_fiscal}>{inv.uuid_fiscal?.slice(0, 8)}...</td>
                        <td className="px-4 py-2"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${t.c}`}>{t.l}</span></td>
                        <td className="px-4 py-2 text-sm text-slate-700">{inv.serie || ''}{inv.folio || ''}</td>
                        <td className="px-4 py-2">
                          <p className="text-sm text-slate-700 truncate max-w-[160px]">{inv.nombre_receptor || '—'}</p>
                          <p className="text-xs text-slate-400">{inv.rfc_receptor}</p>
                        </td>
                        <td className="px-4 py-2 text-sm text-right font-medium text-slate-900">${(inv.total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</td>
                        <td className="px-4 py-2"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.c}`}>{s.l}</span></td>
                        <td className="px-4 py-2 text-sm text-slate-500">{formatDate(inv.stamped_at || inv.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {invoiceTotal > 20 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                  <span className="text-xs text-slate-500">{invoicePage * 20 + 1}-{Math.min((invoicePage + 1) * 20, invoiceTotal)} de {invoiceTotal}</span>
                  <div className="flex gap-1">
                    <button onClick={() => setInvoicePage(Math.max(0, invoicePage - 1))} disabled={invoicePage === 0} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button onClick={() => setInvoicePage(invoicePage + 1)} disabled={(invoicePage + 1) * 20 >= invoiceTotal} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ==================== STAMP ADJUST MODAL ==================== */}
      {showAdjustModal && (
        <StampAdjustModal
          serial={serial}
          onClose={() => setShowAdjustModal(false)}
          onSuccess={() => { setShowAdjustModal(false); loadBalance(); setTxPage(0); loadTransactions(); }}
        />
      )}

      {/* ==================== ASSIGN ADDON MODAL ==================== */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Asignar Addon</h2>
              <button onClick={() => setShowAssignModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleAssignAddon} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Addon *</label>
                <select
                  value={assignForm.addon_code}
                  onChange={(e) => setAssignForm({ ...assignForm, addon_code: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                >
                  <option value="">Seleccionar addon...</option>
                  {availableAddons.map((a) => (
                    <option key={a.code} value={a.code}>{a.name} ({a.code})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notas (opcional)</label>
                <input
                  type="text"
                  value={assignForm.notes}
                  onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                  placeholder="Ej: Incluido en paquete Premium"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium"
                >
                  Asignar
                </button>
                <button
                  type="button"
                  onClick={() => setShowAssignModal(false)}
                  className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default LicenciaGestion;
